import { Box, Divider, Stack, useDisclosure } from "@chakra-ui/react";
import Sidebar from "./components/Sidebar";
import { useState, useEffect } from "react";
import AllRoutes from "./routes/AllRoutes";
import Navbar from "./components/Navbar";
import "./App.css";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import LoginRoute from "./pages/login/LoginRoute";
import Cookies from "js-cookie";
import PasswordSetRoute from "./pages/login/PasswordSetRoute";
import ClientProposalView from "./pages/clientProposal/ClientProposalView";
import { useAuthCheck } from "./hooks/useAuthCheck";
import { useNotificationsSocket } from "./hooks/useNotificationsSocket";
import { isTokenExpired } from "./utils/tokenUtils";
import ClientPaymentPortal from "./pages/clientProposal/ClientPaymentPortal";
import AdmissionPaymentPortal from "./pages/Admission/AdmissionPaymentPortal";
import { useDispatch } from "react-redux";
import { RESET_STORE } from "./redux/store";

function App() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch: any = useDispatch();

  // password set route
  const isPasswordSet = location.pathname === "/password-set";

  // client proposal view route (public, no auth required)
  const isClientProposalView = location.pathname === "/client-proposal-view";
  const isClientProposalPay = matchPath("/payment-portal", location.pathname);
  const isClientAdmissionPay = matchPath(
    "/admission-payment",
    location.pathname,
  );

  const token = Cookies.get("token");

  // Use auth check hook for authenticated routes
  const shouldCheckAuth = token && !isPasswordSet && !isClientProposalView;

  // Only call the hook when needed
  useAuthCheck(!!shouldCheckAuth);
  useNotificationsSocket();

  // Auto-logout after 1 minute of inactivity
  useEffect(() => {
    if (!token || isTokenExpired()) return;

    const IDLE_MS = 15 * 60_000;
    let timeoutId: any = null;

    const clearAllCookies = () => {
      const all = Cookies.get();
      Object.keys(all || {}).forEach((k) => Cookies.remove(k));
      Cookies.remove("token");
      Cookies.remove("signinuser");
    };

    const doLogout = () => {
      clearTimeout(timeoutId);
      clearAllCookies();
      dispatch({ type: RESET_STORE });
      navigate("/login", { replace: true });
    };

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(doLogout, IDLE_MS);
    };

    const activityEvents: (keyof WindowEventMap)[] = [
      "mousemove",
      "mousedown",
      "click",
      "scroll",
      "keydown",
      "touchstart",
    ];

    activityEvents.forEach((evt) =>
      window.addEventListener(evt, resetTimer, { passive: true })
    );
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      activityEvents.forEach((evt) =>
        window.removeEventListener(evt, resetTimer)
      );
    };
  }, [token, dispatch, navigate]);

  if (isClientProposalView) {
    return <ClientProposalView />;
  }
  if (isClientProposalPay) {
    return <ClientPaymentPortal />;
  }
  if (isClientAdmissionPay) {
    return <AdmissionPaymentPortal />;
  }

  // If it's password set page, render it
  if (isPasswordSet) {
    return <PasswordSetRoute />;
  }

  // Check if token exists and is valid
  if (token && !isTokenExpired()) {
    return (
      <Stack h="100%" spacing={0} w="100%" maxW="100vw">
        <Box display="flex" h="100%" flexDirection="column">
          {/* Navbar */}
          <Navbar onOpenSidebar={onOpen} />

          {/* Sidebar Drawer */}
          <Sidebar isOpen={isOpen} onClose={onClose} />

          {/* Main Content */}
          <Box
            flexGrow={1}
            minWidth={0}
            pt="60px" // Account for fixed navbar height
            position="relative"
          >
            <Box minH="100vh" bg="gray.50">
              <Box
                minH="calc(100vh - 60px)"
                borderRadius="md"
                bg={location.pathname === "/" ? "transparent" : "white"}
              >
                <AllRoutes isSideBar={false} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Stack>
    );
  }

  // If no token or expired token, show login
  return <LoginRoute />;
}

export default App;
